import { Text, type Canvas, Control } from 'fabric'
import type {
    TextAlignment,
    FontFamily,
    FabricObjectModifiedEvent,
} from '../../types'
import {
    TEXT_BORDER_STYLES,
    TEXT_OBJECT_DEFAULT_OPTIONS,
    TEXT_OBJECT_PADDING,
    TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
    TEXT_OBJECT_LINE_HEIGHT,
} from '../../constants'
import { clampFontSize } from '../validation'
import { updateAndRenderCanvas } from '../canvas/canvasRendering'
import { CustomText } from './CustomText'

/**
 * Тип для опций конструктора CustomText
 * Включает стандартные свойства Text и кастомные свойства CustomText
 */
type CustomTextOptions = ConstructorParameters<typeof Text>[1] & {
    textBackgroundColor?: string
    textBackgroundPadding?: number
    textBackgroundBorderRadius?: number
}

/**
 * Type guard для проверки, является ли объект экземпляром Text из Fabric.js
 * @param obj - объект для проверки
 * @returns true, если объект является Text
 */
export function isTextObject(obj: unknown): obj is Text {
    return obj instanceof Text
}

/**
 * Получение всех текстовых объектов с canvas
 * Использует type guard вместо type assertion
 * @param canvas - canvas Fabric.js
 * @returns массив текстовых объектов
 */
export function getTextObjects(canvas: Canvas): Text[] {
    const allObjects = canvas.getObjects()
    return allObjects.filter(isTextObject)
}

/**
 * Нормализация имени шрифта для Fabric.js
 * Добавляет fallback для правильной загрузки шрифтов
 * Inter может не загружаться правильно, поэтому добавляем fallback
 * @param fontFamily - семейство шрифтов
 * @returns нормализованное имя шрифта с fallback
 */
export function getNormalizedFontFamily(fontFamily: FontFamily): string {
    // Маппинг шрифтов на их правильные имена с fallback
    // Используем кавычки для шрифтов с пробелами и добавляем fallback
    const fontMap: Record<FontFamily, string> = {
        Inter: "'Inter', sans-serif",
        Onest: "'Onest', sans-serif",
        Times: "'Times New Roman', 'Times', serif",
        Montserrat: "'Montserrat', sans-serif",
    }

    return fontMap[fontFamily] || fontFamily
}

/**
 * Проверка загрузки шрифта через измерение текста
 * Более надежный способ, чем document.fonts.check()
 * @param fontFamily - семейство шрифтов
 * @returns Promise, который резолвится когда шрифт загружен
 */
async function checkFontLoadedByMeasurement(
    fontFamily: FontFamily
): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
        // Создаем временный элемент для проверки загрузки шрифта
        const testElement = document.createElement('span')
        testElement.style.position = 'absolute'
        testElement.style.visibility = 'hidden'
        testElement.style.fontSize = '16px'
        testElement.style.fontFamily = getNormalizedFontFamily(fontFamily)
        testElement.style.whiteSpace = 'nowrap'
        testElement.textContent = 'Mm'
        document.body.appendChild(testElement)

        // Используем requestAnimationFrame для гарантии, что стили применены
        requestAnimationFrame(() => {
            // Получаем размеры текста с нужным шрифтом
            const widthWithFont = testElement.offsetWidth
            const heightWithFont = testElement.offsetHeight

            // Получаем размеры текста с fallback шрифтом (sans-serif)
            testElement.style.fontFamily = 'sans-serif'

            requestAnimationFrame(() => {
                const widthWithFallback = testElement.offsetWidth
                const heightWithFallback = testElement.offsetHeight

                // Удаляем тестовый элемент
                document.body.removeChild(testElement)

                // Если размеры отличаются, значит шрифт загружен
                // Для Inter проверяем более строго - разница должна быть заметной
                const isLoaded =
                    fontFamily === 'Inter'
                        ? Math.abs(widthWithFont - widthWithFallback) > 0.5 ||
                          Math.abs(heightWithFont - heightWithFallback) > 0.5
                        : widthWithFont !== widthWithFallback ||
                          heightWithFont !== heightWithFallback

                resolve(isLoaded)
            })
        })
    })
}

/**
 * Проверка загрузки шрифта
 * @param fontFamily - семейство шрифтов
 * @returns Promise, который резолвится когда шрифт загружен
 */
export async function waitForFontLoad(fontFamily: FontFamily): Promise<void> {
    // Для Inter используем более надежную проверку через измерение
    // Inter может не проходить проверку через document.fonts.check() из-за переменных параметров
    if (fontFamily === 'Inter') {
        // Для Inter всегда ждем немного, чтобы гарантировать загрузку
        // даже если проверка говорит, что шрифт уже загружен
        await new Promise<void>((resolve) => {
            // Сначала проверяем через измерение
            checkFontLoadedByMeasurement(fontFamily).then((isLoaded) => {
                if (isLoaded) {
                    // Если шрифт загружен, все равно ждем немного для гарантии
                    setTimeout(() => {
                        resolve()
                    }, 50)
                } else {
                    // Если шрифт еще не загружен, ждем с повторными проверками
                    let attempts = 0
                    const maxAttempts = 20 // Максимум 2 секунды (20 * 100ms)

                    const checkInterval = setInterval(async () => {
                        attempts++
                        const isLoadedNow =
                            await checkFontLoadedByMeasurement(fontFamily)

                        if (isLoadedNow || attempts >= maxAttempts) {
                            clearInterval(checkInterval)
                            // Даже после загрузки ждем еще немного для гарантии
                            setTimeout(() => {
                                resolve()
                            }, 50)
                        }
                    }, 100)
                }
            })
        })
        return
    }

    // Для остальных шрифтов используем стандартную проверку через Font Loading API
    if (!document.fonts || !document.fonts.check) {
        return Promise.resolve()
    }

    // Маппинг шрифтов на их имена для проверки загрузки
    const fontNameMap: Record<FontFamily, string> = {
        Inter: 'Inter',
        Onest: 'Onest',
        Times: 'Times New Roman',
        Montserrat: 'Montserrat',
    }

    const fontName = fontNameMap[fontFamily] || fontFamily

    // Проверяем, загружен ли шрифт
    if (document.fonts.check(`16px "${fontName}"`)) {
        return Promise.resolve()
    }

    // Ждем загрузки шрифта
    return new Promise<void>((resolve) => {
        // Если fonts.ready уже выполнен, проверяем еще раз
        if (document.fonts.ready) {
            document.fonts.ready
                .then(() => {
                    // После загрузки всех шрифтов проверяем еще раз
                    if (document.fonts.check(`16px "${fontName}"`)) {
                        resolve()
                    } else {
                        // Если шрифт все еще не загружен, ждем еще немного
                        setTimeout(() => {
                            resolve()
                        }, 100)
                    }
                })
                .catch(() => {
                    // В случае ошибки просто продолжаем
                    resolve()
                })
        } else {
            // Если fonts.ready не поддерживается, ждем немного и продолжаем
            setTimeout(() => {
                resolve()
            }, 100)
        }
    })
}

/**
 * Применение стилей обводки к текстовому объекту Fabric.js
 * @param textObj - текстовый объект Fabric.js
 */
export function applyTextBorderStyles(textObj: {
    set: (props: {
        hasBorders: boolean
        borderColor: string
        borderDashArray: number[]
        borderScaleFactor: number
    }) => void
}): void {
    textObj.set({
        hasBorders: true,
        borderColor: TEXT_BORDER_STYLES.COLOR,
        borderDashArray: [...TEXT_BORDER_STYLES.DASH_ARRAY], // Преобразуем readonly в mutable
        borderScaleFactor: TEXT_BORDER_STYLES.SCALE_FACTOR,
    })
}

/**
 * Настройка контролов текстового объекта (скрытие стандартных, добавление кастомного)
 * @param textObj - текстовый объект Fabric.js
 * @param resizeControl - кастомный контрол масштабирования
 */
export function setupTextObjectControls(
    textObj: {
        setControlsVisibility: (visibility: {
            mt: boolean
            mb: boolean
            ml: boolean
            mr: boolean
            tl: boolean
            bl: boolean
            tr: boolean
            br: boolean
            mtr: boolean
        }) => void
        controls: {
            tr?: Control
        }
    },
    resizeControl: Control
): void {
    // Убираем все стандартные контролы, кроме нужных нам
    textObj.setControlsVisibility({
        mt: false, // top
        mb: false, // bottom
        ml: false, // left
        mr: false, // right
        tl: false, // top-left
        bl: false, // bottom-left
        tr: true, // top-right - оставляем для кастомного контрола
        br: false, // bottom-right - убираем
        mtr: false, // rotation
    })

    // Добавляем кастомный контрол в правый верхний угол
    textObj.controls.tr = resizeControl
}

/**
 * Параметры для создания текстового объекта Fabric.js
 */
export interface CreateTextObjectParams {
    /**
     * Текст для отображения
     */
    text: string
    /**
     * Размер шрифта
     */
    fontSize: number
    /**
     * Цвет текста
     */
    fill: string
    /**
     * Выравнивание текста
     */
    textAlign: TextAlignment
    /**
     * Семейство шрифта
     */
    fontFamily: FontFamily
    /**
     * Позиция X в пикселях
     */
    left: number
    /**
     * Позиция Y в пикселях
     */
    top: number
    /**
     * Масштаб по X (по умолчанию 1)
     */
    scaleX?: number
    /**
     * Масштаб по Y (по умолчанию 1)
     */
    scaleY?: number
    /**
     * Цвет подложки за текстом (rgba строка, например, "rgba(5, 111, 255, 0.5)")
     */
    textBackgroundColor?: string | null
    /**
     * Максимальная ширина текста в пикселях (для автоматического переноса строк)
     */
    width?: number
}

/**
 * Создание текстового объекта Fabric.js с базовыми настройками
 * @param params - параметры для создания текстового объекта
 * @returns экземпляр Text для Fabric.js
 */
export function createTextObject(params: CreateTextObjectParams): Text {
    const {
        text,
        fontSize,
        fill,
        textAlign,
        fontFamily,
        left,
        top,
        scaleX = 1,
        scaleY = 1,
        textBackgroundColor = null,
        width,
    } = params

    // Приводим типы для совместимости с Fabric.js
    // TextAlignment уже является 'left' | 'center' | 'right', но TypeScript требует явного приведения
    const fabricTextAlign: 'left' | 'center' | 'right' = textAlign
    const fabricOriginX: 'left' | 'center' | 'right' = textAlign

    // Нормализуем имя шрифта для Fabric.js
    // Inter может не загружаться правильно, поэтому добавляем fallback
    const normalizedFontFamily = getNormalizedFontFamily(fontFamily)

    // Для Inter добавляем отрицательный charSpacing
    // Inter имеет глифы, которые выступают за measureText().width
    // charSpacing сжимает символы и компенсирует эту разницу
    const interCharSpacing = fontFamily === 'Inter' ? -15 : 0

    return new CustomText(text, {
        fontSize: clampFontSize(fontSize),
        // Fabric.js может принимать TFiller | string, но мы используем только строки (hex цвета)
        fill: fill,
        textAlign: fabricTextAlign,
        fontFamily: normalizedFontFamily, // Используем нормализованное имя шрифта
        left,
        top,
        scaleX: scaleX,
        scaleY: scaleY,
        ...TEXT_OBJECT_DEFAULT_OPTIONS,
        // Устанавливаем originX в зависимости от выравнивания
        // Это важно для правильного позиционирования текста при изменении выравнивания
        originX: fabricOriginX,
        borderColor: TEXT_BORDER_STYLES.COLOR,
        borderDashArray: [...TEXT_BORDER_STYLES.DASH_ARRAY],
        borderScaleFactor: TEXT_BORDER_STYLES.SCALE_FACTOR,
        // Добавляем отступ пунктирной линии от текста
        padding: TEXT_OBJECT_PADDING.BORDER,
        // Уменьшаем межстрочное расстояние для слипания подложек
        lineHeight: TEXT_OBJECT_LINE_HEIGHT,
        // Межсимвольное расстояние (для Inter — отрицательное, чтобы компенсировать выход глифов)
        charSpacing: interCharSpacing,
        // Применяем подложку, если она задана
        // Используем type assertion для кастомных свойств CustomText
        textBackgroundColor: textBackgroundColor || undefined,
        // Добавляем отступы для подложки
        // Референс: разрешение 1440x940
        textBackgroundPadding: textBackgroundColor
            ? TEXT_OBJECT_PADDING.BACKGROUND
            : undefined,
        // Скругление углов подложки
        textBackgroundBorderRadius: textBackgroundColor
            ? TEXT_OBJECT_BACKGROUND_BORDER_RADIUS
            : undefined,
        // Ограничиваем ширину текста для автоматического переноса строк
        width: width,
        // Включаем правильную обработку многоязычных символов
        splitByGrapheme: true,
    } as CustomTextOptions)
}

/**
 * Параметры для настройки обработчиков событий текстового объекта
 */
export interface SetupTextObjectEventHandlersParams {
    /**
     * Текстовый объект Fabric.js
     */
    textObj: Text
    /**
     * Canvas Fabric.js
     */
    fabricCanvas: Canvas
    /**
     * Обработчик изменения размера
     */
    onScaling: () => void
    /**
     * Обработчик изменения позиции (опционально)
     */
    onPositionChange?: (e: FabricObjectModifiedEvent) => void
}

/**
 * Настройка обработчиков событий для текстового объекта
 * @param params - параметры для настройки обработчиков
 */
export function setupTextObjectEventHandlers(
    params: SetupTextObjectEventHandlersParams
): void {
    const { textObj, fabricCanvas, onScaling, onPositionChange } = params

    // Обработчик изменения размера
    textObj.on('scaling', onScaling)

    // Обработчик изменения позиции и масштабирования
    // 'object:modified' срабатывает после завершения перетаскивания/масштабирования
    // Это важно для синхронизации слайдера при использовании кастомного контрола
    fabricCanvas.on('object:modified', (e) => {
        // Проверяем, был ли объект масштабирован (scaleX или scaleY не равны 1)
        // Это означает, что пользователь изменил размер через уголок
        if (
            e.target === textObj &&
            (textObj.scaleX !== 1 || textObj.scaleY !== 1)
        ) {
            // Вызываем обработчик масштабирования для синхронизации с ползунком
            onScaling()
        }

        // Вызываем обработчик изменения позиции, если он есть
        if (onPositionChange) {
            onPositionChange(e)
        }
    })

    // Обработчик выделения объекта - применяем настройки обводки сразу
    textObj.on('selected', () => {
        applyTextBorderStyles(textObj)
        updateAndRenderCanvas({ textObj, fabricCanvas })
    })
}
